import { useCallback, useEffect, useState } from "react";

// The outcome of a page-local list read. There is no "idle": the read is
// issued on mount, so the first render is already loading. `failed` is a
// state of its own — a read that errored must never look like an empty list.
export type ListReadStatus = "loading" | "loaded" | "failed";

export interface ListRead<T> {
    items: T[];
    status: ListReadStatus;
    retry: () => void;
}

// One list, read once per mount (and again on retry), held in component state
// rather than a global atom — these browse pages must not write the
// catalogue's productsAtom (#401). `read` must be referentially stable
// (useCallback); a new identity re-issues the read stale-while-revalidate,
// latest-wins. The status is derived from the last settled outcome, which the
// effect only ever writes on settle — never synchronously in its body.
export const useListRead = <T>(read: () => Promise<T[]>): ListRead<T> => {
    const [items, setItems] = useState<T[]>([]);
    const [outcome, setOutcome] = useState<"loaded" | "failed" | null>(null);
    // WHICH read failed, so a `failed` outcome counts only while that read
    // is still the current one. A new read identity (a ship-to switch) is a
    // new read: its outcome is pending, and a stale `failed` — with its live
    // Retry — must not sit over a read that is already in flight. DERIVED,
    // not reset: a render-phase reset keyed on `read` loops when a consumer
    // passes an unstable function, and the effect body never writes state
    // synchronously. Held in a thunk, because setState would CALL a function.
    const [failedRead, setFailedRead] = useState<(() => Promise<T[]>) | null>(
        null,
    );
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        read()
            .then((list) => {
                if (cancelled) return;
                setItems(list);
                setOutcome("loaded");
            })
            .catch(() => {
                // useApi surfaces the toast; the status is the page's signal.
                if (cancelled) return;
                setFailedRead(() => read);
                setOutcome("failed");
            });
        return () => {
            cancelled = true;
        };
    }, [read, attempt]);

    // Back to loading from the handler (not the effect), then re-issue.
    const retry = useCallback(() => {
        setOutcome(null);
        setAttempt((n) => n + 1);
    }, []);

    // A `loaded` outcome is held across a read change (stale-while-revalidate);
    // a `failed` one belongs to the read that failed and reads as loading once
    // another read is current.
    const status: ListReadStatus =
        outcome === "failed"
            ? failedRead === read
                ? "failed"
                : "loading"
            : (outcome ?? "loading");

    return { items, status, retry };
};
